import { useState, useMemo, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useCommitOrAdvance } from '@features/booking/hooks/useCommitOrAdvance';
import { ThreeDSChallengeOverlay } from '@features/booking/components/ThreeDSChallengeOverlay';
import { commitCtaKey } from '@features/booking/utils/commitMomentCopy';
import { verifyAttrs } from '@/shared/verify/core/contract';
import { capturePaymentError } from '@core/sentry';
import { getStripeErrorMessage } from '@features/booking/hooks/useStripeV2';
import { getStripePromise } from '@features/booking/hooks/useStripeV3';
import { usePaymentFee } from '@features/booking/hooks/usePaymentFee';
import { Private } from '@shared/components/common/Private';
import { FeeBreakdownDisplay } from './FeeBreakdownDisplay';
import { formatAmount } from '@shared/utils/formatAmount';
import { PaymentAcceptances } from './PaymentAcceptances';
import { BillingSection } from './BillingSection';
import { useCardElementOptions } from '@features/booking/constants/stripe';
import {
  computeAmexChange,
  getStripePublishableKeyForTokenAccount,
  getStripeTokenAccount,
} from '@features/booking/utils/amexChange';
import { isBlockedBrazilDebit } from '@features/booking/utils/brazilDebit';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { env } from '@core/config/env';
import type { StripeTokenAccount } from '@features/booking/state/types';

type StripePaymentFormInnerProps = {
  stripeTokenAccount: StripeTokenAccount;
  onCardBrandChange: (cardBrand: string | null) => void;
};

function StripePaymentFormInner({
  stripeTokenAccount,
  onCardBrandChange,
}: StripePaymentFormInnerProps) {
  const { t, i18n } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const booking = useBooking();
  const restaurant = useRestaurant();
  const stripe = useStripe();
  const elements = useElements();
  const { feeBreakdown, displayCurrency, slotType, cancellationHours } = usePaymentFee();

  // T14b: this form is the in-flow Guarantee step. When Guarantee is the
  // commit host (Personalize disabled AND skipConfirmStep on), the next
  // step in the active set is BOOKING_FINAL — `useCommitOrAdvance` posts
  // the reservation after tokenization instead of advancing. When
  // Personalize follows (or the flag is off), it falls back to goToNext().
  const {
    commitOrAdvance,
    willCommit,
    nextStepPath,
    isSubmitting,
    error: commitError,
    requireReload,
    isHandling3ds,
  } = useCommitOrAdvance();

  // Ref so handleSubmit can call the LATEST commitOrAdvance after the
  // flushSync below — without this, the commit closure would see the
  // pre-flush booking state and post the reservation without the newly
  // tokenized card. Same pattern as PersonalizePage from T14a.
  const commitOrAdvanceRef = useRef(commitOrAdvance);
  commitOrAdvanceRef.current = commitOrAdvance;

  // Carried-over decline notice, latched ONCE at mount. A one-shot local
  // state (not a derived read of booking.paymentErrorMessage) on purpose:
  // the notice sits at the TOP of the form, so if visibility were derived
  // with the `!error && !commitError` suppression alone, every local-error
  // toggle would unmount/remount it and shift the form under the guest's
  // cursor (e.g. typing the holder name clears `error`, which would bring
  // the stale notice back mid-keystroke). Retired at the next submit —
  // after the guest acts, the notice would describe a previous attempt.
  const [declineNotice, setDeclineNotice] = useState(booking.paymentErrorMessage);

  // Focused on arrival so it is (a) scrolled into view — ScrollToTop pins
  // the redirect at the top of the page — and (b) actually announced:
  // `role="alert"` only fires on insertion/content change, and this block
  // is present in the freshly mounted page's FIRST render, so most screen
  // readers would stay silent without an explicit focus.
  const declineNoticeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (declineNotice) declineNoticeRef.current?.focus();
  }, [declineNotice]);

  const cardOptions = useCardElementOptions();

  // Pre-filled from a previous attempt (non-sensitive) so a guest coming
  // back after a decline only re-enters the card details.
  const [holderName, setHolderName] = useState(booking.cardHolderName ?? '');
  const [allAccepted, setAllAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);

  const feeAmount = feeBreakdown.total;
  const tokenCreatedWithPlatformKey = stripeTokenAccount === 'platform';

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    setCardComplete(event.complete);
    if (event.brand !== 'unknown') {
      onCardBrandChange(event.brand);
    }
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !elements || !allAccepted || !cardComplete) return;

    // Capture commit intent at click time, BEFORE the flushSync below
    // mutates booking.cardToken. Without this snapshot the post-flushSync
    // re-render filters GUARANTEE out of `activeSteps` (its `isRequired`
    // depends on `!hasCardToken`), `currentStepIndex` drops to -1,
    // `getNextStep()` returns null/wrong, and dispatch silently no-ops.
    // Bug reproduced 2026-05-21 by skip-confirm-commit-host.spec.ts.
    // Both `willCommit` AND `nextStepPath` need capturing — the path is
    // what the advance branch navigates to (legacy /confirmation).
    const capturedIntent = { willCommit, nextStepPath };

    setIsProcessing(true);
    setError(null);
    // The guest has acted — the carried-over decline notice now describes a
    // previous attempt. Retire it so it can't flash back mid-processing.
    setDeclineNotice(null);

    if (!holderName.trim()) {
      setError(t('FORM_ERROR_CREDIT_CARD_NAME'));
      setIsProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError(t('PAYMENT_CARD_ERROR'));
      setIsProcessing(false);
      return;
    }

    try {
      const { error: tokenError, token } = await stripe.createToken(cardElement, {
        name: holderName,
      });

      if (tokenError || !token) {
        setError(
          getStripeErrorMessage(
            { code: tokenError?.code, message: tokenError?.message || t('PAYMENT_CARD_ERROR') },
            t,
          ),
        );
        setIsProcessing(false);
        return;
      }

      // feeBreakdown applies the menu→slot guarantee hierarchy; slotType does
      // not. The GUARANTEE step requires a payment-bearing guarantee, so a
      // null type here is an invariant violation, not a fallback case.
      const resolvedGuaranteeType = feeBreakdown.guaranteeType;
      if (resolvedGuaranteeType !== 'deposit' && resolvedGuaranteeType !== 'no_show') {
        Sentry.captureException(
          new Error('Payment submit reached without resolved guarantee type'),
          {
            extra: {
              slotType,
              feeBreakdownGuaranteeType: feeBreakdown.guaranteeType,
              feeBreakdownTotal: feeBreakdown.total,
              restaurantId: restaurant.id,
            },
          },
        );
        setError(t('PAYMENT_CARD_ERROR'));
        setIsProcessing(false);
        return;
      }

      const expectedStripeTokenAccount = getStripeTokenAccount({
        cardBrand: token.card?.brand,
        allowAMEXBR: restaurant.allowAMEXBR,
        guaranteeType: resolvedGuaranteeType,
      });
      if (expectedStripeTokenAccount !== stripeTokenAccount) {
        onCardBrandChange(token.card?.brand ?? null);
        setError(t('PAYMENT_CARD_ERROR'));
        setIsProcessing(false);
        return;
      }

      // Check AMEX restriction (Brazil)
      if (token.card?.brand === 'American Express' && !restaurant.allowAMEXBR && resolvedGuaranteeType === 'deposit') {
        setError(t('STRIPE_BRAZIL_AMEX_ERROR'));
        setIsProcessing(false);
        return;
      }

      // Check debit card restriction (Brazil)
      if (isBlockedBrazilDebit(token.card, restaurant.defaultLanguage)) {
        setError(t('STRIPE_BRAZIL_DEBIT_CARD_ERROR'));
        setIsProcessing(false);
        return;
      }

      // flushSync so the next render publishes the cardToken + fee state
      // before commitOrAdvance reads booking from React context. Without it,
      // when this form is the commit host (skipConfirmStep on, Personalize
      // disabled), the commit would POST without the token we just minted.
      flushSync(() => {
        booking.updateBooking({
          cardToken: token.id,
          paymentMethod: token.card?.brand || null,
          feeDeposit: feeAmount,
          feeGratuity: feeBreakdown.gratuity,
          guaranteeType: resolvedGuaranteeType,
          paymentProcessor: 'stripe',
          stripeTokenAccount,
          amexChange: computeAmexChange({
            cardBrand: token.card?.brand,
            allowAMEXBR: restaurant.allowAMEXBR,
            guaranteeType: resolvedGuaranteeType,
            tokenCreatedWithPlatformKey,
          }),
          // Remember the (non-sensitive) holder name for a potential retry,
          // and retire the previous attempt's decline notice — this submit
          // supersedes it.
          cardHolderName: holderName,
          paymentErrorMessage: null,
        });
      });

      // Post-flushSync: the ref now points at the callback whose closure
      // sees the just-tokenized cardToken. Pass the click-time intent
      // snapshot so dispatch doesn't get fooled by the GUARANTEE-is-now-
      // filtered re-render (see capture comment above).
      await commitOrAdvanceRef.current(capturedIntent);
    } catch (err) {
      // Symmetric with the MP catch block: user-visible copy stays at
      // the translated fallback (raw `err.message` leaks English Stripe
      // SDK strings on Spanish-locale tenants), diagnostic value flows
      // to Sentry via the canonical `capturePaymentError` helper so
      // both processors land on the same `payment.processor`-filtered
      // dashboard with ApiError-aware fingerprinting.
      setError(t('PAYMENT_CARD_ERROR'));
      capturePaymentError(err, 'stripe', {
        restaurantId: restaurant.id,
        stripeTokenAccount,
        stage: 'tokenization-or-commit',
      });
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('[StripePaymentForm] handleSubmit failed:', err);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <BookingPageLayout
      title={t('GRAB_CONFIRMATION_TITLE_4')}
      subtitle={t('GRAB_CONFIRMATION_TITLE_7') || undefined}
      subtitleClassName="text-foreground/70 text-lg mb-6 text-center"
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <div
        className="w-full sm:px-6 space-y-6"
        {...verifyAttrs("StripePaymentForm", {
          willCommit,
          hostStep: "guarantee",
          // T30 D1: the Guarantee step IS a fee-bearing step — feeBreakdown
          // resolves to the deposit total. `hasFee` flips the CTA to
          // "Reservar y pagar $X" when this form is the commit host.
          hasFee: feeBreakdown.total > 0,
          // Surfaced separately from hasFee because the CTA semantic ("are
          // we charging now?") splits on guaranteeType, not on whether a
          // fee is defined: no_show with fee>0 is a hold, not a charge
          // (commit 343e594). The verify matrix uses this to assert the
          // CTA family matches the charge-vs-hold contract.
          guaranteeType: feeBreakdown.guaranteeType ?? "",
          hasError: !!commitError,
          isSubmitting,
          isProcessing,
          requireReload,
          // T30 D2: surfaced for the verify-matrix invariant that gates the
          // ThreeDSChallengeOverlay Portal — overlay must mount iff true.
          isHandling3ds,
          ctaKey: requireReload
            ? "PAYMENT_3DS_RELOAD_TO_RETRY"
            : isProcessing
              ? "PAYMENT_PROCESSING"
              : commitCtaKey({
                  hostStep: "guarantee",
                  // hasFee=true ONLY when the user is being charged the
                  // amount on submit — that's the deposit case. A
                  // no_show guarantee uses the card as a HOLD: nothing
                  // is charged unless the user doesn't show up. Calling
                  // that "Reservar y pagar" would lie (see the
                  // surrounding copy on the page: "You won't be charged
                  // at this moment"). For no_show, render the simpler
                  // "Reservar" copy; the amount stays visible in the
                  // breakdown above the button.
                  hasFee: feeBreakdown.guaranteeType === 'deposit' && feeBreakdown.total > 0,
                  isSubmitting,
                  hasError: !!commitError,
                  isWaitingList: booking.flowType === 'waiting_list',
                }),
        })}
      >
        {booking.flowType === 'waiting_list' ? (
          <div className="text-foreground/80 text-sm space-y-2">
            <p>{t('ADD_CREDIT_CARD_TO_JOIN_WAITING_LIST_1')}</p>
            <p>{t('ADD_CREDIT_CARD_TO_JOIN_WAITING_LIST_2')}</p>
            <p>{t('NO_SHOW_GUARANTEE_APPLICABLE_1')}</p>
            <p>{t('NO_SHOW_GUARANTEE_APPLICABLE_2')}</p>
          </div>
        ) : (
          <div className="text-center text-foreground/70 text-sm">
            <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('NEW_MODAL_PROVIDE_CC_DETAILS')) }} />
            <p>{t('STRING_PAYMENT_CREDITED')}</p>
          </div>
        )}

        {/* Decline notice carried over from a failed commit on ANOTHER page
            (useReservationCommit cleared the consumed token and routed the
            guest back here) — tells them WHY they're re-entering the card.
            Rendered at the TOP: ScrollToTop pins the redirect at the top of
            the page, so the bottom error slot would sit below the fold.
            One-shot local latch (see declineNotice) so it never remounts
            and shifts the form after the guest acts; focused on arrival
            (see declineNoticeRef) for scroll-into-view + SR announcement.
            Gated on the latch ALONE: handleCardChange toggles `error`
            mid-typing (CardElement validation), so an `!error` guard here
            would unmount/remount this block under the guest's cursor. It
            can't coexist with submit-time errors anyway — handleSubmit
            retires the latch before any of them can be set — and stacking
            with a pre-submit card-validation hint is correct: they say
            different things. */}
        {declineNotice && (
          <div
            ref={declineNoticeRef}
            tabIndex={-1}
            role="alert"
            data-testid="payment-decline-error"
            className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive outline-none"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{declineNotice}</span>
          </div>
        )}

        {!stripe ? (
          <div className="text-center text-foreground/50 text-sm py-4">
            {t('PAYMENT_LOADING')}
          </div>
        ) : (
          // Stripe Elements (CardElement below) is iframe-isolated so PAN /
          // CVC / expiry never enter our DOM. BUT cardholder name and
          // BillingSection inputs are in-DOM. Wrap so session-replay tools
          // never capture them.
          <Private className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                {t('PAYMENT_LABEL_CC_2')}
              </label>
              <input
                data-testid="card-holder"
                type="text"
                className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                value={holderName}
                onChange={(e) => {
                  setHolderName(e.target.value);
                  // Clear the "Cardholder name is required" submit error the
                  // moment the user starts typing — standard form UX (RHF /
                  // Material / Polaris all do this): error tells user what's
                  // wrong, fixing the field acknowledges it. Lingering errors
                  // after correction feel like the form is broken. We can't
                  // tell if `error` is THIS field's error or a card-API error,
                  // so we only clear when the value transitions to non-empty
                  // — the only state that resolves THIS particular error.
                  if (e.target.value.trim() && error) setError(null);
                }}
                placeholder="John Doe"
                disabled={isProcessing}
              />
            </div>

            <BillingSection />

            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                {t('PAYMENT_LABEL_CC')}
              </label>
              <div className="border border-border rounded-xl px-4 py-3 focus-within:ring-2 focus-within:ring-brand/20 focus-within:border-brand">
                <CardElement
                  options={cardOptions}
                  onChange={handleCardChange}
                />
              </div>
            </div>
          </Private>
        )}

        <FeeBreakdownDisplay feeBreakdown={feeBreakdown} displayCurrency={displayCurrency} />

        <PaymentAcceptances
          guaranteeType={feeBreakdown.guaranteeType}
          feeAmount={feeAmount}
          displayCurrency={displayCurrency}
          serviceFee={feeBreakdown.serviceFee}
          cancellationHours={cancellationHours}
          isWaitingList={booking.flowType === 'waiting_list'}
          onAllAcceptedChange={setAllAccepted}
        />

        {error && (
          <div
            role="alert"
            className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Commit-host error (only when this form is the commit host —
            the legacy advance branch returns null here). Same styling as
            the local validation error above so commit failures and field
            validation read as the same kind of feedback. */}
        {commitError && (
          <div
            role="alert"
            data-testid="payment-commit-error"
            className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{commitError}</span>
          </div>
        )}

      </div>

      <div className="w-full sm:px-6 mt-8">
        <Button
          data-testid="payment-submit"
          {...verifyAttrs("StripePaymentSubmit", { action: "confirm-booking" })}
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!stripe || !cardComplete || !allAccepted || isProcessing || isSubmitting || requireReload}
        >
          {requireReload
            ? tErrors('PAYMENT_3DS_RELOAD_TO_RETRY')
            : isProcessing
              ? // Local tokenization in flight, before commit fires. Keep
                // the legacy PAYMENT_PROCESSING key — it pre-dates T30 and
                // describes a different phase (Stripe createToken vs the
                // post-tokenization commit POST).
                t('PAYMENT_PROCESSING')
              : // T30 D1: the Guarantee CTA copy reflects what the user is
                // AUTHORIZING ("Reservar y pagar $X") regardless of
                // whether the click commits the reservation now (flag on,
                // Guarantee is the commit host) or routes through
                // /confirmation first (flag off, legacy advance). Either
                // way the fee shown above the button is being charged on
                // submit — "Continue" hides what the user is doing.
                // Earlier this branch was gated on `willCommit` and fell
                // back to NEW_MODAL_CONTINUE in the flag-off case; that
                // was a misreading of T30's CTA table (which keys on
                // hostStep + hasFee, NOT on willCommit).
                t(commitCtaKey({
                  hostStep: 'guarantee',
                  // See verifyAttrs comment above: deposit charges
                  // immediately ("Reservar y pagar $X"), no_show is a
                  // hold ("Reservar"). The page's surrounding copy
                  // already communicates the no_show penalty amount
                  // ("A charge may apply if you don't show up").
                  hasFee: feeBreakdown.guaranteeType === 'deposit' && feeBreakdown.total > 0,
                  isSubmitting,
                  hasError: !!commitError,
                  isWaitingList: booking.flowType === 'waiting_list',
                }), {
                  // For COMMIT_CTA_GUARANTEE_WITH_FEE the spec interpolates
                  // `{{amount}}` via i18next. Safe to pass on every key
                  // — i18next ignores params that don't appear in the
                  // template.
                  amount: `${displayCurrency} ${formatAmount(feeBreakdown.total, i18n.language)}`,
                })}
        </Button>
      </div>

      {/* T30 D2: 3DS overlay. Only flips open when willCommit && a 3DS
          challenge is mid-flight; inert otherwise. */}
      <ThreeDSChallengeOverlay open={isHandling3ds} />
    </BookingPageLayout>
  );
}

export function StripePaymentForm() {
  const restaurant = useRestaurant();
  const { slotType, feeBreakdown } = usePaymentFee();
  const [cardBrand, setCardBrand] = useState<string | null>(null);

  // Picks which Stripe publishable key to load. If this drifts from the
  // submit-time type, expectedStripeTokenAccount in the inner handler will
  // mismatch and abort with a card error instead of charging.
  const renderTimeGuaranteeType = feeBreakdown.guaranteeType ?? slotType;

  const stripeTokenAccount = getStripeTokenAccount({
    cardBrand,
    allowAMEXBR: restaurant.allowAMEXBR,
    guaranteeType: renderTimeGuaranteeType,
  });

  const stripePublishableKey = getStripePublishableKeyForTokenAccount({
    tokenAccount: stripeTokenAccount,
    restaurantPublishableKey: restaurant.publicKeyPayment,
    platformPublishableKey: env.stripeFallbackKey,
  });

  const stripePromise = useMemo(
    () => getStripePromise(stripePublishableKey),
    [stripePublishableKey],
  );

  return (
    <Elements key={stripePublishableKey} stripe={stripePromise}>
      <StripePaymentFormInner
        stripeTokenAccount={stripeTokenAccount}
        onCardBrandChange={setCardBrand}
      />
    </Elements>
  );
}
